import { useState } from 'react';
import { Pressable, ScrollView, StyleSheet, View } from 'react-native';
import { useRouter } from 'expo-router';
import { SafeAreaView } from 'react-native-safe-area-context';
import { ArrowLeft, Layers3 } from 'lucide-react-native';
import { AppButton } from '../components/ui/AppButton';
import { AppCard } from '../components/ui/AppCard';
import { AppText } from '../components/ui/AppText';
import { tokens } from '../theme/tokens';

const cards = [
  {
    id: 'function',
    front: '二次函数顶点式',
    back: 'y = a(x - h)^2 + k，顶点为 (h, k)。',
  },
  {
    id: 'buoyancy',
    front: '浮力公式',
    back: 'F 浮 = ρ液 g V排，注意单位换算。',
  },
];

export default function FlashcardsPage() {
  const router = useRouter();
  const [flippedIds, setFlippedIds] = useState<string[]>([]);

  return (
    <SafeAreaView style={styles.screen}>
      <View style={styles.header}>
        <AppButton
          label="我的"
          variant="ghost"
          onPress={() => router.back()}
          leftSlot={<ArrowLeft size={18} color={tokens.colors.primary} />}
          style={styles.backButton}
        />
        <View style={styles.headerCopy}>
          <AppText variant="title" weight="bold">
            翻卡速记
          </AppText>
          <AppText variant="caption" tone="muted">
            FlashcardScreen 知识点复习
          </AppText>
        </View>
      </View>

      <ScrollView contentContainerStyle={styles.content}>
        <AppCard style={styles.summaryCard}>
          <Layers3 size={24} color={tokens.colors.primary} />
          <View style={styles.summaryCopy}>
            <AppText variant="subtitle" weight="bold">
              知识点复习
            </AppText>
            <AppText variant="body" tone="muted">
              点击卡片翻面，先用本地卡片模拟速记流程。
            </AppText>
          </View>
        </AppCard>

        {cards.map((card) => {
          const flipped = flippedIds.includes(card.id);
          return (
            <Pressable
              key={card.id}
              accessibilityRole="button"
              onPress={() =>
                setFlippedIds((current) =>
                  current.includes(card.id)
                    ? current.filter((id) => id !== card.id)
                    : [...current, card.id]
                )
              }
              style={({ pressed }) => [pressed ? styles.pressed : null]}
            >
              <AppCard style={[styles.flashcard, flipped ? styles.flashcardBack : null]}>
                <AppText variant="caption" tone={flipped ? 'success' : 'primary'} weight="bold">
                  {flipped ? '答案' : '题面'}
                </AppText>
                <AppText variant="subtitle" weight="bold" align="center">
                  {flipped ? card.back : card.front}
                </AppText>
              </AppCard>
            </Pressable>
          );
        })}
      </ScrollView>
    </SafeAreaView>
  );
}

const styles = StyleSheet.create({
  screen: {
    flex: 1,
    backgroundColor: tokens.colors.background,
  },
  header: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: tokens.spacing.md,
    paddingHorizontal: tokens.layout.screenPadding,
    paddingTop: tokens.spacing.md,
    paddingBottom: tokens.spacing.sm,
  },
  backButton: {
    minHeight: 40,
    paddingHorizontal: tokens.spacing.sm,
  },
  headerCopy: {
    flex: 1,
  },
  content: {
    paddingHorizontal: tokens.layout.screenPadding,
    paddingTop: tokens.spacing.md,
    paddingBottom: tokens.spacing.xxl,
    gap: tokens.spacing.md,
  },
  summaryCard: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: tokens.spacing.md,
  },
  summaryCopy: {
    flex: 1,
    gap: tokens.spacing.xs,
  },
  flashcard: {
    minHeight: 160,
    alignItems: 'center',
    justifyContent: 'center',
    gap: tokens.spacing.md,
  },
  flashcardBack: {
    backgroundColor: tokens.colors.successSoft,
  },
  pressed: {
    opacity: 0.86,
  },
});
